import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import {
  AccountBalance,
  Store,
  CreditCard,
  ContentCopy,
  Phone,
  LocationOn,
  Schedule,
} from '@mui/icons-material';
import {
  PaymentMethodsResponse,
  PaymentMethodType,
} from '../../entities/PaymentMethods';

interface PaymentMethodsDisplayProps {
  paymentMethods: PaymentMethodsResponse;
  onMethodSelect?: (methodType: PaymentMethodType) => void;
  highlightMethod?: PaymentMethodType;
  showOnlyHighlighted?: boolean; // Nueva prop para mostrar solo el método resaltado
}

export const PaymentMethodsDisplay: React.FC<PaymentMethodsDisplayProps> = ({
  paymentMethods,
  onMethodSelect,
  highlightMethod,
  showOnlyHighlighted = false, // Por defecto mostrar todos
}) => {
  const { company_info, available_methods } = paymentMethods;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // TODO: Agregar notificación de éxito
  };

  const formatBankInfo = (info: string) => {
    // Formatear CBU y otras info bancaria
    if (info.length === 22) {
      // Es un CBU
      return `${info.slice(0, 4)} ${info.slice(4, 8)} ${info.slice(8, 16)} ${info.slice(16)}`;
    }
    return info;
  };

  // Determinar qué métodos mostrar
  const shouldShowBankTransfer = showOnlyHighlighted
    ? highlightMethod === 'bank_transfer' && available_methods.bank_transfer
    : available_methods.bank_transfer;

  const shouldShowCashPayment = showOnlyHighlighted
    ? highlightMethod === 'cash_payment' && available_methods.cash_payment
    : available_methods.cash_payment;

  const shouldShowMercadoPago = showOnlyHighlighted
    ? highlightMethod === 'mercadopago' &&
      available_methods.mercadopago?.available
    : available_methods.mercadopago?.available;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          mb: 3,
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
          letterSpacing: '-0.01em',
        }}
      >
        Métodos de pago disponibles
      </Typography>

      {/* Información de la empresa */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {company_info.legal_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {company_info.tax_id_type}: {company_info.tax_id_number}
        </Typography>
      </Alert>

      <Stack spacing={2}>
        {/* Transferencia Bancaria */}
        {shouldShowBankTransfer && (
          <Card
            sx={{
              borderRadius: 3,
              border: '2px solid',
              borderColor:
                highlightMethod === 'bank_transfer'
                  ? 'primary.main'
                  : 'rgba(0, 0, 0, 0.08)',
              backgroundColor: 'background.paper', // Siempre fondo blanco
              boxShadow:
                highlightMethod === 'bank_transfer'
                  ? '0 8px 25px rgba(25, 118, 210, 0.15)' // Sombra azul sutil cuando está resaltado
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s ease',
              cursor: showOnlyHighlighted
                ? 'default'
                : onMethodSelect
                  ? 'pointer'
                  : 'default',
              '&:hover':
                !showOnlyHighlighted && onMethodSelect
                  ? {
                      transform: 'translateY(-2px)',
                      boxShadow:
                        highlightMethod === 'bank_transfer'
                          ? '0 12px 35px rgba(25, 118, 210, 0.2)'
                          : '0 8px 25px rgba(0, 0, 0, 0.1)',
                    }
                  : {},
            }}
            onClick={
              showOnlyHighlighted
                ? undefined
                : () => onMethodSelect?.('bank_transfer')
            }
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <AccountBalance
                  sx={{ color: 'primary.main', fontSize: '1.5rem' }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  }}
                >
                  Transferencia Bancaria
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Banco
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {available_methods.bank_transfer?.bank_name}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Titular de la cuenta
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {available_methods.bank_transfer?.account_holder_name}
                  </Typography>
                </Box>

                <Divider />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      CBU
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          userSelect: 'text',
                          cursor: 'text',
                        }}
                      >
                        {available_methods.bank_transfer?.cbu || ''}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            available_methods.bank_transfer?.cbu || ''
                          );
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <ContentCopy sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Alias
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          userSelect: 'text',
                          cursor: 'text',
                        }}
                      >
                        {available_methods.bank_transfer?.alias}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            available_methods.bank_transfer?.alias || ''
                          );
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <ContentCopy sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Número de cuenta
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {available_methods.bank_transfer?.account_number}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      CUIL/CUIT
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {available_methods.bank_transfer?.cuil}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Pago Presencial */}
        {shouldShowCashPayment && (
          <Card
            sx={{
              borderRadius: 3,
              border: '2px solid',
              borderColor:
                highlightMethod === 'cash_payment'
                  ? 'secondary.main'
                  : 'rgba(0, 0, 0, 0.08)',
              backgroundColor: 'background.paper', // Siempre fondo blanco
              boxShadow:
                highlightMethod === 'cash_payment'
                  ? '0 8px 25px rgba(156, 39, 176, 0.15)' // Sombra morada sutil cuando está resaltado
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s ease',
              cursor: showOnlyHighlighted
                ? 'default'
                : onMethodSelect
                  ? 'pointer'
                  : 'default',
              '&:hover':
                !showOnlyHighlighted && onMethodSelect
                  ? {
                      transform: 'translateY(-2px)',
                      boxShadow:
                        highlightMethod === 'cash_payment'
                          ? '0 12px 35px rgba(156, 39, 176, 0.2)'
                          : '0 8px 25px rgba(0, 0, 0, 0.1)',
                    }
                  : {},
            }}
            onClick={
              showOnlyHighlighted
                ? undefined
                : () => onMethodSelect?.('cash_payment')
            }
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Store sx={{ color: 'secondary.main', fontSize: '1.5rem' }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  }}
                >
                  Pago Presencial
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Box>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <LocationOn
                      sx={{
                        color: 'text.secondary',
                        fontSize: '1.125rem',
                        mt: 0.25,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          userSelect: 'text',
                          cursor: 'text',
                        }}
                      >
                        {available_methods.cash_payment?.address}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          userSelect: 'text',
                          cursor: 'text',
                        }}
                      >
                        {available_methods.cash_payment?.city},{' '}
                        {available_methods.cash_payment?.state}{' '}
                        {available_methods.cash_payment?.postal_code}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Schedule
                      sx={{
                        color: 'text.secondary',
                        fontSize: '1.125rem',
                        mt: 0.25,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Horarios de atención
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          whiteSpace: 'pre-line',
                          userSelect: 'text',
                          cursor: 'text',
                        }}
                      >
                        {available_methods.cash_payment?.business_hours}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Phone
                      sx={{ color: 'text.secondary', fontSize: '1.125rem' }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Teléfono de contacto
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          userSelect: 'text',
                          cursor: 'text',
                        }}
                      >
                        {available_methods.cash_payment?.contact_phone}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {available_methods.cash_payment?.additional_instructions && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      {available_methods.cash_payment?.additional_instructions}
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* MercadoPago */}
        {shouldShowMercadoPago && (
          <Card
            sx={{
              borderRadius: 3,
              border: '2px solid',
              borderColor:
                highlightMethod === 'mercadopago'
                  ? 'info.main'
                  : 'rgba(0, 0, 0, 0.08)',
              backgroundColor: 'background.paper', // Siempre fondo blanco
              boxShadow:
                highlightMethod === 'mercadopago'
                  ? '0 8px 25px rgba(2, 136, 209, 0.15)' // Sombra cyan sutil cuando está resaltado
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s ease',
              cursor: showOnlyHighlighted
                ? 'default'
                : onMethodSelect
                  ? 'pointer'
                  : 'default',
              '&:hover':
                !showOnlyHighlighted && onMethodSelect
                  ? {
                      transform: 'translateY(-2px)',
                      boxShadow:
                        highlightMethod === 'mercadopago'
                          ? '0 12px 35px rgba(2, 136, 209, 0.2)'
                          : '0 8px 25px rgba(0, 0, 0, 0.1)',
                    }
                  : {},
            }}
            onClick={
              showOnlyHighlighted
                ? undefined
                : () => onMethodSelect?.('mercadopago')
            }
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <CreditCard sx={{ color: 'info.main', fontSize: '1.5rem' }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  }}
                >
                  MercadoPago
                </Typography>
              </Stack>

              <Typography variant="body1" color="text.secondary">
                {available_methods.mercadopago?.description}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default PaymentMethodsDisplay;
